import { generationModelSupportsVision, type GenerationModel } from '~/design-system/components/DeckGen/ai/deckGenSchema'

/**
 * REFERENCE IMAGES — the pictures the MODEL LOOKS AT while it writes an artifact.
 *
 * Not to be confused with images IN a document: the contract still forbids those (SELF-CONTAINED, one
 * exception for verified logos). A reference is read and then thrown away; nothing from it is embedded.
 * What survives is the look the model took from it, expressed in the tokens and markup it writes.
 *
 * SHARED BY BOTH ARTIFACT ROUTES, and that is the reason this is a module rather than a function inside
 * one. The lab proved reference images work and the canvas now ships them, so two copies of the caps would
 * mean the lab measuring a limit the product does not enforce — the lab's entire value is that what it
 * measures is what production does.
 *
 * Pure: values in, values out, no request object and no provider call, so a node test asserts the same
 * behaviour the routes get.
 */

/** FOUR REFERENCES, and the number is a judgement rather than a provider limit.
 *
 *  A reference set is read for one feel. Past about four, the model has to average several looks that
 *  disagree, and the average of four aesthetics is mush — the same reason a moodboard with thirty tiles
 *  produces a blander deck than one with six. Four also covers every real case: a look, its dark variant, a
 *  detail crop, and the thing being edited. Anything beyond that is a moodboard and belongs in the theme
 *  recipe, which is the channel built for many sources. */
export const MAX_REFERENCE_IMAGES = 4

/** Base64 CHARACTERS, not decoded bytes: ~3MB of image once decoded, comfortably under the 5MB-per-image
 *  ceiling Anthropic enforces and far under anything a slide screenshot needs (a 1920x1080 PNG lands around
 *  300–600KB). It is a guard against a pasted 40-megapixel photo blowing the request, not a real budget. */
export const MAX_REFERENCE_IMAGE_CHARS = 4_000_000

/** Providers reject on TOTAL request size, not per part, so the per-image cap alone would still let four
 *  large references add up past Gemini's ~20MB inline-request ceiling. ~7.5MB decoded across the whole set. */
export const MAX_REFERENCE_TOTAL_CHARS = 10_000_000

/** http as well as https, because a harness serving fixtures off localhost is normal in the lab. */
const HTTP_URL_RE = /^https?:\/\/[^\s"'<>]+$/i

/** Anchored and bounded, so the engine reads ~30 characters and stops. The base64 BODY is deliberately not
 *  validated: scanning four megabytes to pre-empt a malformed payload costs more than letting the provider
 *  return the 400 it would return anyway, and a regex over a 4MB string is exactly the kind of thing that
 *  turns a press of Generate into a hung request. */
const DATA_IMAGE_RE = /^data:image\/(png|jpe?g|webp|gif|avif);base64,[A-Za-z0-9+/=]/i

function readStringList(value: unknown, max: number): string[] {
  if (!Array.isArray(value)) return []
  const out: string[] = []
  for (const entry of value) {
    if (out.length >= max) break
    if (typeof entry !== 'string') continue
    const clean = entry.trim()
    if (clean !== '' && !out.includes(clean)) out.push(clean)
  }
  return out
}

/**
 * Data URLs or http(s) URLs, deduped, capped by count and by total size, and dropped entirely for a
 * text-only model rather than sent and rejected.
 *
 * READS MORE CANDIDATES THAN THE CAP and counts only the VALID ones. Capping the intake instead would let a
 * single junk entry at the head of the array silently cost a real reference at the tail, which reads to the
 * user as "the model ignored my fourth image" and is unfindable from the UI.
 *
 * `continue`, never `break`, on an oversized entry, for the same reason: one 40MB paste in the middle of the
 * list must not swallow the small references behind it.
 */
export function readArtifactReferenceImages(value: unknown, model: GenerationModel, label: string): string[] {
  if (!generationModelSupportsVision(model)) {
    if (Array.isArray(value) && value.length > 0) {
      console.warn(`[${label}] ${model} is text-only — dropping ${value.length} reference image(s)`)
    }
    return []
  }
  const out: string[] = []
  let total = 0
  for (const entry of readStringList(value, MAX_REFERENCE_IMAGES * 4)) {
    if (out.length >= MAX_REFERENCE_IMAGES) break
    const isData = DATA_IMAGE_RE.test(entry)
    if (!isData && !(entry.length <= 500 && HTTP_URL_RE.test(entry))) continue
    if (isData && entry.length > MAX_REFERENCE_IMAGE_CHARS) continue
    if (total + entry.length > MAX_REFERENCE_TOTAL_CHARS) continue
    total += entry.length
    out.push(entry)
  }
  return out
}

/**
 * What the model is TOLD the pictures are for.
 *
 * Without this the images arrive as bare parts and the model guesses: most often it treats a screenshot as
 * content to transcribe, and writes a slide ABOUT the reference instead of a slide that LOOKS like it. The
 * distinction is the whole feature, so it is stated rather than hoped for.
 *
 * Empty for no images, so callers concatenate unconditionally.
 */
export function referenceImagesPromptBlock(count: number): string {
  if (count <= 0) return ''
  const noun = count === 1 ? 'image is' : 'images are'
  return [
    `REFERENCE ${count === 1 ? 'IMAGE' : 'IMAGES'}. ${count} ${noun} attached to this message, and ${count === 1 ? 'it is' : 'they are'} a LOOK to build in, never content to describe.`,
    'Read the composition, the palette, the type treatment, the spacing rhythm, the weight and the amount of empty space, then write a document that feels like it belongs beside them. Take the STRUCTURE of the reference, not its subject.',
    'Never transcribe what the reference says. Never mention the reference, the brand in it, or the fact that a picture was provided. The slide is about the brief; the picture only decides how it looks.',
    'Never embed or link the reference itself: the no-images rule is unchanged, so whatever you take from it has to be rebuilt in type, divs, gradients and inline SVG.',
    'Where the reference and a THEME CONTEXT block disagree, the theme context wins, since that is the deck this slide has to live in.',
  ].join('\n')
}
